/**
 * Creating a programme: the form's state, and the one call that saves it.
 *
 * @module router/programmes/CreateProgrammeRoute
 */

import React, { useRef, useState } from "react";

import { useNavigate } from "react-router";

import {
  CreateProgrammeForm,
  type ImportSummary,
  type InitialModuleDraft,
  type ProgrammeCreationMode,
} from "@/components/programmes/CreateProgrammeForm";
import { ProgrammeCreatedSummary } from "@/components/programmes/ProgrammeCreatedSummary";
import { useAsyncAction } from "@/components/ui";
import { importProgrammeFromJson } from "@/export/json";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import { can } from "@/server/authorization";
import { defaultProgramme } from "@/state/store";
import type { Module, Programme } from "@/types";
import { begin } from "@/utils/begin";
import { uid } from "@/utils/uid";

export function CreateProgrammeRoute(): React.ReactElement {
  const navigate = useNavigate();
  const { client, session, profile, platformUsers, refreshSession } = useWorkspaceOutletContext();

  const [mode, setMode] = useState<ProgrammeCreationMode>("scratch");
  const [title, setTitle] = useState("");
  const [initialModules, setInitialModules] = useState<InitialModuleDraft[]>([]);
  const [imported, setImported] = useState<Programme | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | undefined>(undefined);
  const [importError, setImportError] = useState("");
  // Set only when someone else leads it: its creator lands on a read-only editor.
  const [createdFor, setCreatedFor] = useState<{
    id: string;
    title: string;
    leadName: string;
  } | null>(null);

  // A client-admin creates programmes for other people to lead, so it picks
  // one; anyone else leads what they create.
  const chooseLead = profile.role === "client-admin";
  const leads = platformUsers.filter((user) => user.status === "active");
  // Not a useState default: the tenant's users arrive after the first render,
  // so seeding from them once leaves the field empty for ever and the form
  // unsubmittable. The picked value wins.
  const [pickedLeadId, setLeadId] = useState("");
  // Until one is picked the creator stands in, never `leads[0]`, which is the
  // creator only while the tenant holds one user and otherwise hands the
  // programme to a stranger its author cannot edit.
  const standIn = leads.find((lead) => lead.id === session.user.id);
  const leadId = leads.some((lead) => lead.id === pickedLeadId)
    ? pickedLeadId
    : (standIn?.id ?? "");

  const create = useAsyncAction(async () => {
    const id = uid("programme");
    const modules: Module[] = initialModules.map((draft) => ({
      id: uid("mod"),
      code: "",
      title: draft.title.trim(),
      credits: 0,
      isElective: false,
      mimlos: [],
      assessments: [],
    }));
    if (mode === "import" && !imported) {
      // Rather than falling through to an empty programme, which would look
      // like the import silently losing every module in the document.
      throw new Error("Choose a programme JSON document to import.");
    }
    // The mode, not just the value: an import that resolves after the user has
    // switched back to "scratch" still sets `imported`, and keying off it alone
    // would save the document they abandoned and drop the modules they typed.
    const structured =
      mode === "import" && imported
        ? { ...structuredClone(imported), id, title: title.trim() }
        : { ...defaultProgramme(), id, title: title.trim(), modules };

    const leadUserId = chooseLead ? leadId : session.user.id;
    await client.createProgramme({ id, structured, leadUserId });
    // The session, and only the session: creating a programme grants the
    // creator its `lead` responsibility, and every permission the editor
    // checks comes from `session.subject`, so without this they open what they
    // just made read-only. Refreshing the workspace as well is redundant —
    // resolving the session re-fires that fetch on its own.
    await refreshSession();
    if (leadUserId !== session.user.id) {
      const lead = leads.find((user) => user.id === leadUserId);
      setCreatedFor({
        id,
        title: structured.title,
        leadName: lead?.displayName ?? lead?.email ?? "the chosen lead",
      });
      return;
    }
    navigate(`/programmes/${id}/overview/identity`);
  });

  // The same guard `useAsync` carries. A read is abandoned by switching mode
  // or by picking a second file, and without this the abandoned one still
  // lands: it would replace a title typed afterwards with the document walked
  // away from, and reopen the submit gate for a file no longer chosen.
  const generation = useRef(0);

  const readImport = (file: File | null): void => {
    generation.current += 1;
    const mine = generation.current;
    setImportError("");
    // Picking again is a new attempt, so the last one's failure goes with it.
    // Left up it sits beside the import's own message, and both are assertive
    // regions, so they announce over each other.
    create.reset();
    if (!file) {
      setImported(null);
      setImportSummary(undefined);
      return;
    }
    begin(() => importProgrammeFromJson(file)).then((result) => {
      if (generation.current !== mine) {
        return;
      }
      if (!result.success || !result.programme) {
        setImported(null);
        setImportSummary(undefined);
        // The typed title stays: only a successful import has a title of its
        // own to replace it with, and clearing it here made the submit gate
        // report a second thing missing that the user had already given.
        setImportError(result.error ?? "That file is not a valid programme JSON document.");
        return;
      }
      const programme = result.programme as Programme;
      setImported(programme);
      setImportSummary({
        fileName: file.name,
        moduleCount: programme.modules?.length ?? 0,
        versionCount: programme.versions?.length ?? 0,
        schemaVersion: programme.schemaVersion,
      });
      setTitle(typeof programme.title === "string" ? programme.title.trim() : "");
    });
  };

  const changeMode = (next: ProgrammeCreationMode): void => {
    generation.current += 1;
    setMode(next);
    setImportError("");
    // The other mode's failure is not this mode's; leaving it up puts an alert
    // about a scratch creation above the import panel.
    create.reset();
    // The typed title and the module drafts stay. The two cards are a native
    // radio group, so an ArrowRight to read the other option selects it, and
    // clearing here threw away everything typed with no warning and no undo.
    // `readImport` already states this policy for the import path.
    if (next === "scratch") {
      setImported(null);
      setImportSummary(undefined);
    }
  };

  if (createdFor) {
    // Read at render, not in the action: the session refreshed after it ran.
    return (
      <ProgrammeCreatedSummary
        programmeId={createdFor.id}
        programmeTitle={createdFor.title}
        leadName={createdFor.leadName}
        canOpen={can(session.subject, "programme:write", {
          type: "programme",
          programmeId: createdFor.id,
        })}
        backTo={profile.role === "client-admin" ? "/admin/programmes" : "/programmes"}
      />
    );
  }

  return (
    <CreateProgrammeForm
      mode={mode}
      onModeChange={changeMode}
      title={title}
      onTitleChange={setTitle}
      leads={
        chooseLead
          ? leads.map((u) => ({ id: u.id, displayName: u.displayName, email: u.email }))
          : undefined
      }
      leadId={leadId}
      onLeadChange={setLeadId}
      leadName={session.user.displayName ?? session.user.email}
      initialModules={initialModules}
      onAddModule={() => {
        setInitialModules((current) => [...current, { id: uid("module-draft"), title: "" }]);
      }}
      onModuleTitleChange={(id, next) => {
        setInitialModules((current) =>
          current.map((module) => (module.id === id ? { ...module, title: next } : module)),
        );
      }}
      onRemoveModule={(id) => {
        setInitialModules((current) => current.filter((module) => module.id !== id));
      }}
      importSummary={importSummary}
      onImportFile={readImport}
      importError={importError || undefined}
      submitting={create.pending}
      error={create.error?.message}
      onSubmit={() => {
        create.run().catch(() => undefined);
      }}
      onCancel={() => {
        navigate("/programmes");
      }}
    />
  );
}
